import React from "react";
import "./style/PrivacyPolicy.css";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  return (
    <div className="privacy-policy-container">
      <div className="privacy-policy-content">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: May 2025</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Welcome to Quanto Retail. We are committed to protecting your
            privacy and ensuring you have a positive experience on our website
            and while using our services. This Privacy Policy explains how we
            collect, use, disclose, and safeguard your information when you
            visit our website (quantoretail.com) and use our retail management
            software solutions.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>

          <h3>2.1 Information You Provide Directly</h3>
          <ul>
            <li>
              <strong>Contact Forms:</strong> When you fill out our contact
              forms, demo requests, or inquiry forms, we collect your name,
              email address, phone number, company name, and any other
              information you voluntarily provide.
            </li>
            <li>
              <strong>Account Information:</strong> If you create an account to
              use our services, we collect login credentials, business
              information, payment details, and profile information.
            </li>
            <li>
              <strong>Communication Data:</strong> When you communicate with us
              via email, chat, phone, or WhatsApp, we collect the content of
              your messages and communication history.
            </li>
            <li>
              <strong>Support Requests:</strong> Technical support inquiries,
              feedback, and customer service interactions are recorded.
            </li>
          </ul>

          <h3>2.2 Information Collected Automatically</h3>
          <ul>
            <li>
              <strong>Browser & Device Information:</strong> IP address, browser
              type, operating system, device type, and device identifiers.
            </li>
            <li>
              <strong>Usage Data:</strong> Pages visited, time spent on pages,
              links clicked, features used, and navigation patterns.
            </li>
            <li>
              <strong>Cookies & Tracking Technologies:</strong> We use cookies,
              web beacons, and similar technologies to track user behavior and
              preferences.
            </li>
            <li>
              <strong>Location Data:</strong> We may collect general location
              information based on IP address (city/region level, not precise
              GPS).
            </li>
          </ul>

          <h3>2.3 Information from Third Parties</h3>
          <ul>
            <li>Analytics providers and marketing partners</li>
            <li>Social media platforms (if you connect your account)</li>
            <li>Payment processors and financial institutions</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect for the following purposes:</p>
          <ul>
            <li>
              Providing, maintaining, and improving our software and services
            </li>
            <li>Processing orders, payments, and delivering services</li>
            <li>Responding to your inquiries and providing customer support</li>
            <li>
              Sending promotional emails, newsletters, and marketing
              communications (with your consent)
            </li>
            <li>
              Personalizing your experience and tailoring content to your
              interests
            </li>
            <li>
              Analyzing usage patterns to improve our website and services
            </li>
            <li>
              Detecting and preventing fraud, abuse, and security incidents
            </li>
            <li>
              Complying with legal obligations and enforcing our terms of
              service
            </li>
            <li>Creating analytics reports and insights about our user base</li>
          </ul>
        </section>

        <section>
          <h2>4. How We Share Your Information</h2>
          <p>
            We do not sell your personal information. However, we may share your
            information with:
          </p>
          <ul>
            <li>
              <strong>Service Providers:</strong> Third-party vendors who assist
              us with payment processing, email delivery, hosting, analytics,
              and customer support.
            </li>
            <li>
              <strong>Business Partners:</strong> With your consent, we may
              share information with partners relevant to your business needs.
            </li>
            <li>
              <strong>Legal Requirements:</strong> When required by law, court
              order, or government request.
            </li>
            <li>
              <strong>Business Transfers:</strong> In case of merger,
              acquisition, or sale of assets.
            </li>
            <li>
              <strong>Public Information:</strong> Information you choose to
              make public on your profile or testimonials.
            </li>
          </ul>
        </section>

        <section>
          <h2>5. Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your
            personal information from unauthorized access, alteration,
            disclosure, or destruction. These include:
          </p>
          <ul>
            <li>SSL/TLS encryption for data transmission</li>
            <li>Secure password requirements and authentication</li>
            <li>Regular security audits and vulnerability assessments</li>
            <li>Restricted access to personal data on a need-to-know basis</li>
            <li>Firewalls and intrusion detection systems</li>
          </ul>
          <p>
            <strong>Note:</strong> While we strive to protect your information,
            no method of transmission over the internet or electronic storage is
            100% secure. We cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>6. Cookies & Tracking Technologies</h2>
          <p>
            Our website uses cookies to enhance your experience. Cookies are
            small data files stored on your device. You can control cookies
            through your browser settings, but disabling cookies may affect
            website functionality.
          </p>
          <h3>Types of Cookies We Use:</h3>
          <ul>
            <li>
              <strong>Essential Cookies:</strong> Required for basic website
              functionality
            </li>
            <li>
              <strong>Analytics Cookies:</strong> Help us understand how users
              interact with our site
            </li>
            <li>
              <strong>Marketing Cookies:</strong> Used for targeted advertising
              and retargeting
            </li>
            <li>
              <strong>Preference Cookies:</strong> Remember your settings and
              preferences
            </li>
          </ul>
        </section>

        <section>
          <h2>7. Your Data Rights</h2>
          <p>Depending on your location, you may have the following rights:</p>
          <ul>
            <li>
              <strong>Right to Access:</strong> Request a copy of the personal
              data we hold about you
            </li>
            <li>
              <strong>Right to Correction:</strong> Request correction of
              inaccurate data
            </li>
            <li>
              <strong>Right to Deletion:</strong> Request deletion of your
              personal data (subject to legal obligations)
            </li>
            <li>
              <strong>Right to Opt-Out:</strong> Unsubscribe from marketing
              communications
            </li>
            <li>
              <strong>Right to Data Portability:</strong> Request your data in a
              portable format
            </li>
            <li>
              <strong>Right to Withdraw Consent:</strong> Withdraw previously
              given consent
            </li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at
            <a href="mailto:support@quantoretail.com">
              {" "}
              support@quantoretail.com
            </a>
          </p>
        </section>

        <section>
          <h2>8. Third-Party Links</h2>
          <p>
            Our website may contain links to third-party websites and social
            media platforms. This Privacy Policy does not apply to external
            websites. We are not responsible for their privacy practices. Please
            review their privacy policies before sharing any information.
          </p>
        </section>

        <section>
          <h2>9. Children's Privacy</h2>
          <p>
            Our services are not intended for individuals under 13 years old. We
            do not knowingly collect personal information from children. If we
            become aware that a child has provided us with personal information,
            we will take steps to delete such information and terminate the
            child's account.
          </p>
        </section>

        <section>
          <h2>10. Data Retention</h2>
          <p>
            We retain your personal information for as long as necessary to:
          </p>
          <ul>
            <li>Fulfill the purposes outlined in this policy</li>
            <li>Comply with legal and regulatory requirements</li>
            <li>Resolve disputes and enforce agreements</li>
          </ul>
          <p>
            After the retention period, we will securely delete or anonymize
            your information. Some data may be retained longer due to legal
            obligations.
          </p>
        </section>

        <section>
          <h2>11. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries
            other than your country of residence. These countries may have data
            protection laws that differ from your home country. By using our
            services, you consent to such transfers. We ensure appropriate
            safeguards are in place.
          </p>
        </section>

        <section>
          <h2>12. Updates to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy periodically to reflect changes in
            our practices, technology, legal requirements, and other factors. We
            will notify you of material changes by updating the "Last Updated"
            date and, if required, by sending you an email notification. Your
            continued use of our services after changes constitute your
            acceptance of the updated Privacy Policy.
          </p>
        </section>

        <section>
          <h2>13. Contact Us</h2>
          <p>
            If you have questions, concerns, or requests regarding this Privacy
            Policy or our privacy practices, please contact us:
          </p>
          <div className="contact-info">
            <p>
              <strong>Email:</strong>{" "}
              <a href="mailto:support@quantoretail.com">
                support@quantoretail.com
              </a>
            </p>
            <p>
              <strong>WhatsApp Support:</strong>
              <br />
              Support: +91 9384030115 | +91 9384030116 | +91 9384030117
              <br />
              Sales: +91 9384030113 | +91 8667875166 | +91 7904159558
            </p>
            <p>
              <strong>Website:</strong>{" "}
              <a href="https://www.quantoretail.com">www.quantoretail.com</a>
            </p>
          </div>
        </section>

        <section>
          <h2>14. Compliance with Regulations</h2>
          <p>
            We comply with applicable privacy laws and regulations including:
          </p>
          <ul>
            <li>General Data Protection Regulation (GDPR) - for EU users</li>
            <li>
              California Consumer Privacy Act (CCPA) - for California residents
            </li>
            <li>Information Technology Act, 2000 (ITA) - for India</li>
            <li>
              Personal Data Protection Act (PDPA) - for relevant jurisdictions
            </li>
          </ul>
        </section>

        <div className="privacy-policy-footer">
          <Link to="/" className="back-home-link">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
